/**
 * RelatorioG2BP - Relatório simplificado do G2 Behavioral Profile
 * Versão inicial com análise básica DISC
 */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { 
  FileText, 
  Download, 
  Loader2, 
  User,
  Briefcase,
  Calendar,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';

// Cores dos traços DISC
const TRAIT_COLORS = {
  D: '#dc2626',
  I: '#f59e0b',
  S: '#22c55e',
  C: '#3b82f6'
};

// Perfis DISC
const DISC_PROFILES = {
  D: {
    nome: 'Dominância',
    descricao: 'Perfil orientado para resultados, desafios e tomada de decisão rápida.',
    caracteristicas: ['Determinado', 'Competitivo', 'Direto', 'Decisivo', 'Corajoso'],
    pontosFortes: ['Liderança natural', 'Foco em resultados', 'Tomada de decisão rápida', 'Iniciativa'],
    pontosAtencao: ['Pode parecer autoritário', 'Impaciência', 'Delegar mais'],
    motivadores: ['Desafios', 'Autonomia', 'Resultados', 'Controle'],
    comunicacao: 'Seja direto, objetivo e focado em resultados. Evite rodeios.'
  },
  I: {
    nome: 'Influência',
    descricao: 'Perfil comunicativo, entusiasta e focado em relacionamentos.',
    caracteristicas: ['Comunicativo', 'Otimista', 'Persuasivo', 'Sociável', 'Entusiasta'],
    pontosFortes: ['Excelente networking', 'Motivação de equipes', 'Criatividade', 'Persuasão'],
    pontosAtencao: ['Organização', 'Foco em detalhes', 'Gestão do tempo'],
    motivadores: ['Reconhecimento', 'Interação social', 'Liberdade', 'Novidades'],
    comunicacao: 'Seja amigável e aberto. Permita expressão e reconheça contribuições.'
  },
  S: {
    nome: 'Estabilidade',
    descricao: 'Perfil paciente, leal e focado em harmonia e cooperação.',
    caracteristicas: ['Paciente', 'Leal', 'Cooperativo', 'Tranquilo', 'Confiável'],
    pontosFortes: ['Harmonia da equipe', 'Confiabilidade', 'Suporte aos colegas', 'Persistência'],
    pontosAtencao: ['Adaptação a mudanças', 'Assertividade', 'Tomar iniciativa'],
    motivadores: ['Estabilidade', 'Relacionamentos', 'Segurança', 'Reconhecimento sincero'],
    comunicacao: 'Seja paciente e mostre interesse. Dê tempo para processar mudanças.'
  },
  C: {
    nome: 'Conformidade',
    descricao: 'Perfil analítico, detalhista e focado em qualidade e precisão.',
    caracteristicas: ['Analítico', 'Preciso', 'Organizado', 'Sistemático', 'Detalhista'],
    pontosFortes: ['Atenção aos detalhes', 'Análise crítica', 'Padrões de qualidade', 'Organização'],
    pontosAtencao: ['Paralisia por análise', 'Flexibilidade', 'Velocidade de decisão'],
    motivadores: ['Qualidade', 'Precisão', 'Reconhecimento técnico', 'Processos claros'],
    comunicacao: 'Seja preciso e baseado em fatos. Forneça dados e tempo para análise.'
  }
};

// Combinações de perfis
const PERFIL_COMBINACOES = {
  'DI': { nome: 'Inspirador', descricao: 'Combina assertividade com entusiasmo. Lidera motivando pessoas.' },
  'DC': { nome: 'Realizador', descricao: 'Une determinação com precisão. Focado em resultados de qualidade.' },
  'ID': { nome: 'Promotor', descricao: 'Comunicativo e orientado para ação. Persuasivo e direto.' },
  'IS': { nome: 'Conselheiro', descricao: 'Amigável e solidário. Excelente para relacionamentos e suporte.' },
  'IC': { nome: 'Comunicador Analítico', descricao: 'Criativo com atenção aos detalhes.' },
  'SD': { nome: 'Especialista', descricao: 'Paciente mas determinado. Consistente na busca de objetivos.' },
  'SI': { nome: 'Facilitador', descricao: 'Harmonizador natural. Cria ambientes positivos.' },
  'SC': { nome: 'Detalhista Intelectual', descricao: 'Metódico e confiável. Focado em qualidade.' },
  'CD': { nome: 'Perfeccionista Assertivo', descricao: 'Exigente e direto. Busca excelência.' },
  'CI': { nome: 'Avaliador', descricao: 'Analítico mas comunicativo.' },
  'CS': { nome: 'Especialista Técnico', descricao: 'Preciso e paciente.' }
};

// Função para gerar PDF - Algoritmo DISC Ipsativo v2.0
const generateG2BPPDF = async (data, psicologo) => {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = 210;
  const margin = 15;
  const contentWidth = pageWidth - (margin * 2);
  
  // Detectar versão do resultado e extrair dados apropriados
  const isV2 = data.versao === '2.0';
  const percentuaisSintese = isV2 ? data.percentuaisSintese : (data.percentages || { D: 25, I: 25, S: 25, C: 25 });
  const percentuaisMais = isV2 ? data.percentuaisMais : percentuaisSintese;
  const percentuaisMenos = isV2 ? data.percentuaisMenos : percentuaisSintese;
  const scoresSintese = isV2 ? data.scoresSintese : (data.scores || { D: 0, I: 0, S: 0, C: 0 });
  
  const dominantProfile = DISC_PROFILES[data.dominant];
  
  // Determinar estilo combinado
  const sorted = Object.entries(percentuaisSintese).sort(([,a], [,b]) => b - a);
  const estilo = sorted[1][1] >= sorted[0][1] * 0.7 ? sorted[0][0] + sorted[1][0] : sorted[0][0];
  const estiloInfo = PERFIL_COMBINACOES[estilo] || { nome: dominantProfile.nome, descricao: dominantProfile.descricao };
  
  // Helper para converter hex para RGB
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 0, g: 0, b: 0 };
  };
  
  // ================== PÁGINA 1: CAPA ==================
  // Background
  pdf.setFillColor(79, 70, 229); // Indigo
  pdf.rect(0, 0, pageWidth, 297, 'F');
  
  // Elementos decorativos
  pdf.setFillColor(99, 102, 241);
  pdf.circle(pageWidth - 30, 50, 60, 'F');
  pdf.setFillColor(129, 140, 248);
  pdf.circle(20, 270, 40, 'F');
  
  // Título
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(28);
  pdf.setFont('helvetica', 'bold');
  pdf.text('G2 BEHAVIORAL', pageWidth/2, 70, { align: 'center' });
  pdf.setFontSize(24);
  pdf.text('PROFILE', pageWidth/2, 82, { align: 'center' });
  
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'normal');
  pdf.text('Relatório de Avaliação Comportamental', pageWidth/2, 100, { align: 'center' });
  
  // Box de informações
  pdf.setFillColor(255, 255, 255);
  pdf.roundedRect(margin + 15, 120, contentWidth - 30, 90, 5, 5, 'F');
  
  pdf.setTextColor(79, 70, 229);
  pdf.setFontSize(18);
  pdf.setFont('helvetica', 'bold');
  pdf.text(data.nome.toUpperCase(), pageWidth/2, 145, { align: 'center' });
  
  pdf.setTextColor(100, 116, 139);
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'normal');
  if (data.cargo) pdf.text(data.cargo, pageWidth/2, 160, { align: 'center' });
  pdf.text(`Avaliação: ${data.dataAvaliacao}`, pageWidth/2, 175, { align: 'center' });
  
  pdf.setTextColor(79, 70, 229);
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text(`Perfil: ${estiloInfo.nome}`, pageWidth/2, 195, { align: 'center' });
  
  // Alerta de inconsistência na capa se aplicável
  if (data.inconsistente) {
    pdf.setFillColor(251, 191, 36);
    pdf.roundedRect(margin + 20, 220, contentWidth - 40, 20, 3, 3, 'F');
    pdf.setTextColor(120, 53, 15);
    pdf.setFontSize(10);
    pdf.text('PERFIL SOB ANÁLISE - Padrões atípicos detectados', pageWidth/2, 232, { align: 'center' });
  }
  
  // Logo
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(20);
  pdf.text('G2eGente', pageWidth/2, 260, { align: 'center' });
  pdf.setFontSize(10);
  pdf.text('Plataforma de Recrutamento Ético', pageWidth/2, 270, { align: 'center' });
  
  // ================== PÁGINA 2: GRÁFICOS ==================
  pdf.addPage();
  let y = margin;
  
  // Header
  pdf.setFillColor(79, 70, 229);
  pdf.rect(0, 0, pageWidth, 18, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.text('G2 Behavioral Profile', margin, 12);
  pdf.text(data.nome, pageWidth - margin, 12, { align: 'right' });
  
  y = 30;
  
  // Alerta de inconsistência detalhado se aplicável
  if (data.inconsistente) {
    pdf.setFillColor(254, 243, 199);
    pdf.roundedRect(margin, y, contentWidth, 25, 3, 3, 'F');
    pdf.setDrawColor(251, 191, 36);
    pdf.setLineWidth(0.5);
    pdf.roundedRect(margin, y, contentWidth, 25, 3, 3, 'S');
    
    pdf.setTextColor(180, 83, 9);
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'bold');
    pdf.text('ATENÇÃO: Perfil Sob Análise', margin + 5, y + 8);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    const alertLines = pdf.splitTextToSize(
      `Este resultado apresenta padrões atípicos. ${data.motivoInconsistencia || 'Recomenda-se uma segunda avaliação.'}`,
      contentWidth - 10
    );
    pdf.text(alertLines, margin + 5, y + 15);
    y += 35;
  }
  
  pdf.setTextColor(79, 70, 229);
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text('OS TRÊS VETORES DO SEU PERFIL', margin, y);
  y += 10;
  
  pdf.setTextColor(100, 116, 139);
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.text('Metodologia DISC Ipsativo (Escolha Forçada) - Normalizado 0% a 100%', margin, y);
  y += 12;
  
  // Função auxiliar para desenhar gráfico de barras
  const drawBarChart = (startY, percentages, title, bgColor, titleColor) => {
    const barWidth = 35;
    const barHeight = 50;
    const startX = margin + 10;
    
    // Título do gráfico
    pdf.setFillColor(...bgColor);
    pdf.roundedRect(margin, startY, contentWidth, 8, 2, 2, 'F');
    pdf.setTextColor(...titleColor);
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    pdf.text(title, margin + 5, startY + 6);
    
    const chartY = startY + 15;
    
    ['D', 'I', 'S', 'C'].forEach((trait, index) => {
      const x = startX + (index * (barWidth + 10));
      const score = percentages[trait] || 0;
      const barH = (score / 100) * barHeight;
      const rgb = hexToRgb(TRAIT_COLORS[trait]);
      
      // Fundo
      pdf.setFillColor(241, 245, 249);
      pdf.rect(x, chartY, barWidth, barHeight, 'F');
      
      // Barra
      pdf.setFillColor(rgb.r, rgb.g, rgb.b);
      pdf.rect(x, chartY + (barHeight - barH), barWidth, barH, 'F');
      
      // Label
      pdf.setTextColor(rgb.r, rgb.g, rgb.b);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.text(trait, x + barWidth/2, chartY + barHeight + 8, { align: 'center' });
      
      // Percentual
      pdf.setTextColor(50, 50, 50);
      pdf.setFontSize(9);
      pdf.text(`${score}%`, x + barWidth/2, chartY - 3, { align: 'center' });
    });
    
    return chartY + barHeight + 15;
  };
  
  // Gráfico 1: Síntese (Perfil Composto)
  y = drawBarChart(y, percentuaisSintese, 'PERFIL COMPOSTO (SÍNTESE) - Mais - Menos', [79, 70, 229], [255, 255, 255]);
  y += 5;
  
  // Gráfico 2: Mais (Perfil Público)
  y = drawBarChart(y, percentuaisMais, 'PERFIL PÚBLICO (MAIS) - Como você se apresenta', [34, 197, 94], [255, 255, 255]);
  y += 5;
  
  // Gráfico 3: Menos (Perfil Natural)
  y = drawBarChart(y, percentuaisMenos, 'PERFIL NATURAL (MENOS) - Comportamento sob pressão', [239, 68, 68], [255, 255, 255]);
  
  // Footer página 2
  pdf.setFontSize(8);
  pdf.setTextColor(100, 116, 139);
  pdf.text('Algoritmo: DISC Ipsativo v2.0 | Normalização: 0-100%', margin, 285);
  pdf.text('Página 2', pageWidth - margin, 285, { align: 'right' });
  
  // ================== PÁGINA 3: ANÁLISE ==================
  pdf.addPage();
  y = margin;
  
  // Header
  pdf.setFillColor(79, 70, 229);
  pdf.rect(0, 0, pageWidth, 18, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.text('G2 Behavioral Profile', margin, 12);
  pdf.text(data.nome, pageWidth - margin, 12, { align: 'right' });
  
  y = 30;
  pdf.setTextColor(79, 70, 229);
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text('ANÁLISE DO PERFIL', margin, y);
  y += 15;
  
  // Perfil identificado
  const rgb = hexToRgb(TRAIT_COLORS[data.dominant]);
  pdf.setFillColor(rgb.r, rgb.g, rgb.b);
  pdf.roundedRect(margin, y, contentWidth, 10, 2, 2, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'bold');
  pdf.text(`PERFIL IDENTIFICADO: ${estiloInfo.nome.toUpperCase()}`, margin + 5, y + 7);
  y += 18;
  
  pdf.setTextColor(50, 50, 50);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  const descLines = pdf.splitTextToSize(estiloInfo.descricao, contentWidth - 10);
  pdf.text(descLines, margin + 5, y);
  y += descLines.length * 5 + 12;
  
  // Características
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(79, 70, 229);
  pdf.text('CARACTERÍSTICAS PRINCIPAIS:', margin, y);
  y += 8;
  
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(50, 50, 50);
  dominantProfile.caracteristicas.forEach(carac => {
    pdf.text(`• ${carac}`, margin + 5, y);
    y += 6;
  });
  
  y += 8;
  
  // Pontos Fortes
  pdf.setFillColor(220, 252, 231);
  pdf.roundedRect(margin, y, contentWidth, 8, 2, 2, 'F');
  pdf.setTextColor(21, 128, 61);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.text('PONTOS FORTES', margin + 5, y + 6);
  y += 15;
  
  pdf.setTextColor(50, 50, 50);
  pdf.setFont('helvetica', 'normal');
  dominantProfile.pontosFortes.forEach(ponto => {
    pdf.text(`✓ ${ponto}`, margin + 5, y);
    y += 6;
  });
  
  y += 8;
  
  // Pontos de Atenção
  pdf.setFillColor(254, 243, 199);
  pdf.roundedRect(margin, y, contentWidth, 8, 2, 2, 'F');
  pdf.setTextColor(180, 83, 9);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.text('PONTOS DE ATENÇÃO', margin + 5, y + 6);
  y += 15;
  
  pdf.setTextColor(50, 50, 50);
  pdf.setFont('helvetica', 'normal');
  dominantProfile.pontosAtencao.forEach(ponto => {
    pdf.text(`⚠ ${ponto}`, margin + 5, y);
    y += 6;
  });
  
  y += 10;
  
  // Motivadores
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(79, 70, 229);
  pdf.text('MOTIVADORES:', margin, y);
  y += 8;
  
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(50, 50, 50);
  pdf.text(dominantProfile.motivadores.join(' • '), margin + 5, y);
  y += 15;
  
  // Comunicação
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(79, 70, 229);
  pdf.text('COMO SE COMUNICAR:', margin, y);
  y += 8;
  
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(50, 50, 50);
  const comLines = pdf.splitTextToSize(dominantProfile.comunicacao, contentWidth - 10);
  pdf.text(comLines, margin + 5, y);
  
  // Footer com assinatura
  pdf.setDrawColor(200, 200, 200);
  pdf.line(margin, 265, pageWidth - margin, 265);
  
  pdf.setFontSize(8);
  pdf.setTextColor(100, 116, 139);
  pdf.text(`Relatório gerado em: ${new Date().toLocaleDateString('pt-BR')} | Versão do Algoritmo: ${data.versao || '1.0'}`, margin, 272);
  
  // Assinatura
  if (psicologo) {
    pdf.setDrawColor(200, 200, 200);
    pdf.line(pageWidth - margin - 60, 275, pageWidth - margin, 275);
    pdf.setFontSize(10);
    pdf.setTextColor(50, 50, 50);
    pdf.setFont('helvetica', 'bold');
    pdf.text(psicologo.nome, pageWidth - margin, 280, { align: 'right' });
    if (psicologo.crp) {
      pdf.setFont('helvetica', 'normal');
      pdf.text(`CRP: ${psicologo.crp}`, pageWidth - margin, 286, { align: 'right' });
    }
  }
  
  pdf.text('Página 3', pageWidth - margin, 292, { align: 'right' });
  
  return pdf;
};

export default function RelatorioG2BP({ 
  open, 
  onClose, 
  invite, 
  psychologists = [],
  crpObrigatorio = false
}) {
  const [loading, setLoading] = useState(false);
  const [psicologoSelecionado, setPsicologoSelecionado] = useState(null);
  const [nomeManual, setNomeManual] = useState('');
  const [crpManual, setCrpManual] = useState('');
  const [usarManual, setUsarManual] = useState(psychologists.length === 0);

  const g2bpResult = invite?.resultado?.g2bp_result;
  
  if (!g2bpResult) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resultado não disponível</DialogTitle>
          </DialogHeader>
          <p className="text-slate-500">
            O resultado do G2BP não está disponível para este convite.
          </p>
          <DialogFooter>
            <Button onClick={onClose}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  const dominantProfile = DISC_PROFILES[g2bpResult.dominant];

  const dadosRelatorio = {
    nome: invite.nome_destinatario,
    cargo: invite.cargo_funcao || '',
    dataAvaliacao: invite.responded_at 
      ? new Date(invite.responded_at).toLocaleDateString('pt-BR', { 
          day: '2-digit', month: 'long', year: 'numeric' 
        })
      : new Date().toLocaleDateString('pt-BR'),
    dominant: g2bpResult.dominant,
    // Scores brutos (v2.0)
    scoresMais: g2bpResult.scoresMais,
    scoresMenos: g2bpResult.scoresMenos,
    scoresSintese: g2bpResult.scoresSintese,
    // Percentuais normalizados (v2.0)
    percentuaisMais: g2bpResult.percentuaisMais,
    percentuaisMenos: g2bpResult.percentuaisMenos,
    percentuaisSintese: g2bpResult.percentuaisSintese,
    // Compatibilidade com v1.0
    scores: g2bpResult.scores || g2bpResult.scoresSintese,
    percentages: g2bpResult.percentages || g2bpResult.percentuaisSintese,
    // Estilo e validação
    estilo: g2bpResult.estilo,
    inconsistente: g2bpResult.inconsistente || false,
    motivoInconsistencia: g2bpResult.motivoInconsistencia || null,
    versao: g2bpResult.versao || '1.0'
  };

  const getPsicologoData = () => {
    if (usarManual) {
      if (!nomeManual.trim()) return null;
      return { nome: nomeManual.trim(), crp: crpManual.trim() || null };
    }
    if (psicologoSelecionado) {
      const psi = psychologists.find(p => p.id === psicologoSelecionado);
      return psi ? { nome: psi.nome, crp: psi.crp } : null;
    }
    return null;
  };

  const canGenerate = () => {
    const psi = getPsicologoData();
    if (!psi) return false;
    if (crpObrigatorio && !psi.crp) return false;
    return true;
  };

  const handleGenerate = async () => {
    const psicologo = getPsicologoData();
    if (!psicologo) {
      toast.error('Informe o responsável pelo relatório');
      return;
    }
    if (crpObrigatorio && !psicologo.crp) {
      toast.error('CRP é obrigatório para este relatório');
      return;
    }

    setLoading(true);
    try {
      const pdf = await generateG2BPPDF(dadosRelatorio, psicologo);
      
      const fileName = `Relatorio_G2BP_${invite.nome_destinatario.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      
      toast.success('Relatório G2BP gerado com sucesso!');
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
      toast.error('Erro ao gerar relatório');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-indigo-600" />
            Gerar Relatório G2 Behavioral Profile
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Preview dos dados */}
          <div className="bg-gradient-to-r from-indigo-50 to-violet-50 rounded-lg p-4 border border-indigo-200">
            <h4 className="font-semibold text-indigo-800 mb-3 flex items-center gap-2">
              <User className="w-4 h-4" />
              Dados do Avaliado
            </h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-slate-400" />
                <span><strong>Nome:</strong> {invite.nome_destinatario}</span>
              </div>
              <div className="flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-slate-400" />
                <span><strong>Cargo:</strong> {invite.cargo_funcao || 'Não informado'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-400" />
                <span><strong>Data:</strong> {dadosRelatorio.dataAvaliacao}</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-indigo-500" />
                <span><strong>Perfil:</strong></span>
                <Badge className="bg-indigo-100 text-indigo-700">
                  {g2bpResult.estilo || g2bpResult.dominant} - {dominantProfile?.nome}
                </Badge>
              </div>
            </div>
          </div>

          {/* Prévia do perfil */}
          <div className="bg-white rounded-lg p-4 border">
            <h4 className="font-semibold text-slate-700 mb-3">Prévia do Perfil</h4>
            <div className="flex justify-around items-end h-20 bg-slate-50 rounded p-2">
              {['D', 'I', 'S', 'C'].map((letra) => (
                <div key={letra} className="flex flex-col items-center">
                  <div 
                    className="w-8 rounded-t transition-all"
                    style={{ 
                      height: `${(g2bpResult.percentages?.[letra] || 25) * 0.6}px`,
                      backgroundColor: TRAIT_COLORS[letra]
                    }}
                  />
                  <span className="text-xs font-bold mt-1" style={{ color: TRAIT_COLORS[letra] }}>{letra}</span>
                  <span className="text-xs text-slate-500">{g2bpResult.percentages?.[letra] || 25}%</span>
                </div>
              ))}
            </div>
          </div>

          {/* Seleção de Psicólogo */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-slate-700">Responsável pelo Relatório</h4>
              {crpObrigatorio && (
                <Badge variant="outline" className="text-amber-600 border-amber-300">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  CRP Obrigatório
                </Badge>
              )}
            </div>

            {psychologists.length > 0 && (
              <div className="flex gap-2">
                <Button
                  variant={!usarManual ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setUsarManual(false)}
                >
                  Psicólogo Cadastrado
                </Button>
                <Button
                  variant={usarManual ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setUsarManual(true)}
                >
                  Informar Manualmente
                </Button>
              </div>
            )}

            {!usarManual && psychologists.length > 0 ? (
              <div className="space-y-2">
                <Label>Selecione o Psicólogo</Label>
                <select
                  value={psicologoSelecionado || ''}
                  onChange={(e) => setPsicologoSelecionado(e.target.value)}
                  className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">Selecione...</option>
                  {psychologists.map((psi) => (
                    <option key={psi.id} value={psi.id}>
                      {psi.nome} - CRP: {psi.crp}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nome do Responsável *</Label>
                  <Input
                    value={nomeManual}
                    onChange={(e) => setNomeManual(e.target.value)}
                    placeholder="Nome completo"
                  />
                </div>
                <div className="space-y-2">
                  <Label>CRP {crpObrigatorio && '*'}</Label>
                  <Input
                    value={crpManual}
                    onChange={(e) => setCrpManual(e.target.value.toUpperCase())}
                    placeholder="Ex: 06/123456"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button 
            onClick={handleGenerate}
            disabled={loading || !canGenerate()}
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Gerando...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Gerar PDF
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
